import { motion } from "motion/react";
import _cloneDeep from "lodash/cloneDeep";
import { useEffect, useState } from "react";
import { MINIMUM_ANSWERING_TIME } from ".";
import { convertS3UrlToCDN } from "../../../../../../utils/media";
import { toggleArrayValue, updateArray } from "../../../../../../utils/misc";
import { ButtonLoadingEllipsis } from "../../../../../StoreComponents/StoreButton";
import { FlowSolidButtonGrey } from "../../components/FlowButtons";
import SurveyNpsCheckbox from "../components/SurveyNpsCheckbox";
import SurveySelectRow from "../components/SurveySelectRow";
import SurveyTimingNextButton from "../components/SurveyTimingButton";
import { useSurvey } from "./SurveyProvider";
import { FlowActionButtonsGroup } from "../../components/FlowStyledComponents";
import StoreInput from "../../../../../StoreComponents/StoreInput";

const SelectQuestion = () => {
  const {
    currentStep,
    moveToNextStep,
    moveToPreviousStep,
    question,
    answers,
    setAnswers,
    timer,
    setTimer,
    submitting,
  } = useSurvey();
  const ans = answers[currentStep]?.selectedOptions ?? [];
  const mode = question?.responseOptionsMetaData?.layoutMode;
  const isNPS = mode === "nps";
  const isCondensed = mode === "condensedChoices";
  const isOtherOptionEnabled =
    question?.responseOptionsMetaData?.isOtherOptionEnabled;

  // State for "Other" option
  const [otherText, setOtherText] = useState("");
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const saveAnswer = (updatedAns: string[]) => {
    setAnswers((state) => {
      return updateArray(state, currentStep, {
        type: "multipleChoice",
        selectedOptions: updatedAns,
      });
    });
  };

  const handleItemClick = (str: string) => {
    if (str === "Other") {
      setIsOtherSelected(!isOtherSelected);
      if (!isOtherSelected) {
        // Adding "Other" option
        const updatedAns = question?.canSelectMultipleResponses
          ? [...ans, "Other"]
          : ["Other"];
        saveAnswer(updatedAns);
      } else {
        // Removing "Other" option - also remove any custom text that was entered
        const updatedAns = ans.filter(
          (option) =>
            option !== "Other" &&
            !(
              option !== "Other" &&
              !question?.responseOptions?.includes(option) &&
              option.trim().length > 0
            )
        );
        saveAnswer(updatedAns);
        setOtherText(""); // Clear the text input
      }
    } else {
      // Handle regular options
      const updatedAns = question?.canSelectMultipleResponses
        ? toggleArrayValue(ans, str)
        : [str];
      saveAnswer(updatedAns);

      // If selecting a regular option and it's single choice, deselect "Other"
      if (!question?.canSelectMultipleResponses && str !== "Other") {
        setIsOtherSelected(false);
        setOtherText("");
      }
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    // Don't update the answer immediately - wait for blur or next button
  };

  const handleOtherTextBlur = () => {
    // Update the answer when user finishes typing
    if (isOtherSelected && otherText.trim().length > 0) {
      const updatedAns = ans.map((option) =>
        option === "Other" ? otherText.trim() : option
      );
      saveAnswer(updatedAns);
    }
  };

  const handleNextClick = () => {
    // Save the "Other" text before moving to next question
    if (isOtherSelected && otherText.trim().length > 0) {
      const updatedAns = ans.map((option) =>
        option === "Other" ? otherText.trim() : option
      );
      saveAnswer(updatedAns);
    }
    // save and move to next
    moveToNextStep();
  };
  const handleBackClick = () => {
    moveToPreviousStep();
  };

  // Sync state when current step changes
  useEffect(() => {
    const currentAnswer = answers[currentStep]?.selectedOptions ?? [];
    const otherSelected = currentAnswer.includes("Other");

    // Also check if there's custom text that was entered for "Other" option
    const hasCustomOtherText = currentAnswer.some(
      (option) =>
        option !== "Other" &&
        !question?.responseOptions?.includes(option) &&
        option.trim().length > 0
    );

    const isOtherActuallySelected = otherSelected || hasCustomOtherText;
    setIsOtherSelected(isOtherActuallySelected);

    // If "Other" is selected or has custom text, find the custom text
    if (isOtherActuallySelected) {
      const otherTextValue =
        currentAnswer.find(
          (option) =>
            option !== "Other" && !question?.responseOptions?.includes(option)
        ) || "";
      setOtherText(otherTextValue);
    } else {
      setOtherText("");
    }
  }, [currentStep, answers, question?.responseOptions]);

  useEffect(() => {
    let interval: any = null;

    interval = setInterval(() => {
      const timerClone = _cloneDeep(timer);
      if (timerClone[currentStep] < MINIMUM_ANSWERING_TIME) {
        timerClone[currentStep] = timerClone[currentStep] + 1;
        setTimer(timerClone);
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, timer[currentStep]]);
  const answerTime = timer[currentStep];

  // Check if "Other" is selected but no text is provided (only when special "Other" with text input is shown)
  const hasOtherWithoutText = isOtherOptionEnabled && isOtherSelected && otherText.trim().length === 0;
  const hasValidAnswer = ans.length > 0 && !hasOtherWithoutText;

  const disableNext =
    answerTime < MINIMUM_ANSWERING_TIME ? true : !hasValidAnswer;

  return (
    <>
      <motion.div
        key={question?.description}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`question select ${isNPS || isCondensed ? "nps" : ""}`}
      >
        <div className="title">{question?.description}</div>

        <div className={`options ${question?.imageUrl ? "with_image" : ""}`}>
          {question?.imageUrl ? (
            <div>
              <img
                className="question_image"
                alt="question_image"
                src={convertS3UrlToCDN(question.imageUrl)}
              />
            </div>
          ) : null}
          {isNPS ||
          (question?.imageUrl &&
            !question?.canSelectMultipleResponses) ? null : (
            <div
              className="subtitle"
              style={{ marginTop: 0, marginBottom: 15 }}
            >
              {question?.canSelectMultipleResponses
                ? "Select all that apply"
                : "Select one"}
            </div>
          )}
          {isNPS || isCondensed ? (
            <>
              {isNPS ? (
                <div className="nps_subtitle">
                  <div>Not likely</div>
                  <div>Extremely likely</div>
                </div>
              ) : null}
              <div
                style={isNPS ? undefined : { flexWrap: "wrap" }}
                className="select_options nps"
              >
                {question?.responseOptions?.map((option) => {
                  const selected = ans.includes(option);
                  return (
                    <SurveyNpsCheckbox
                      key={option}
                      text={option}
                      onClick={() => {
                        handleItemClick(option);
                      }}
                      // disabled={disabled}
                      selected={selected}
                    />
                  );
                })}

                {/* Other Option for NPS (though it shouldn't typically be enabled) */}
                {isOtherOptionEnabled && !isNPS && (
                  <>
                    <SurveyNpsCheckbox
                      key="Other"
                      text="Other"
                      onClick={() => {
                        handleItemClick("Other");
                      }}
                      selected={isOtherSelected}
                    />
                  </>
                )}
              </div>

              {/* Text input for Other option in NPS layout */}
              {isOtherOptionEnabled && !isNPS && isOtherSelected && (
                <StoreInput
                  value={otherText}
                  placeholder="Please specify..."
                  textFieldStyle={{ minWidth: "100%" }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleOtherTextChange(e.target.value)
                  }
                  onBlur={handleOtherTextBlur}
                />
              )}
            </>
          ) : (
            <>
              <div className="select_options">
                {question?.responseOptions?.map((option) => {
                  const selected = ans.includes(option);
                  const disabled =
                    ans.length > 0 &&
                    !selected &&
                    !question.canSelectMultipleResponses;
                  return (
                    <SurveySelectRow
                      key={option}
                      text={option}
                      onClick={() => {
                        handleItemClick(option);
                      }}
                      disabled={disabled}
                      selected={selected}
                    />
                  );
                })}

                {/* Other Option */}
                {isOtherOptionEnabled && (
                  <>
                    <SurveySelectRow
                      key="Other"
                      text="Other"
                      onClick={() => {
                        handleItemClick("Other");
                      }}
                      disabled={
                        ans.length > 0 &&
                        !isOtherSelected &&
                        !question.canSelectMultipleResponses
                      }
                      selected={isOtherSelected}
                    />
                    {isOtherSelected && (
                      <StoreInput
                        value={otherText}
                        textFieldStyle={{ minWidth: "100%" }}
                        placeholder="Please specify..."
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleOtherTextChange(e.target.value)
                        }
                        onBlur={handleOtherTextBlur}
                      />
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
      <FlowActionButtonsGroup>
        <FlowSolidButtonGrey disabled={submitting} onClick={handleBackClick}>
          Back
        </FlowSolidButtonGrey>
        <SurveyTimingNextButton
          answerTime={answerTime}
          onClick={handleNextClick}
          disabled={disableNext}
          text={
            submitting ? (
              <>
                Submitting
                <ButtonLoadingEllipsis baseFontSize={16} />
              </>
            ) : (
              "Next"
            )
          }
        />
      </FlowActionButtonsGroup>
    </>
  );
};

export default SelectQuestion;
